import React, { useState, useEffect } from 'react';
import Header from './Header.jsx';
import { Filter } from './Filter.jsx';
import { Categories } from './Categories.jsx';
import EventCard from './EventCard.jsx';
import { useLoadingContext } from './LoadingContext.jsx';
import { domain } from './Domain.jsx';
import useApiRequest from './useApiRequest.jsx';
import { useParams } from 'react-router-dom';
import { useFilterContext } from './FilterContext.jsx';
import { filterEvents } from './EventFilter.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import Footer from './Footer.jsx';
import { ChevronLeft, ChevronRight, Calendar, MapPin, Users, Filter as FilterIcon } from 'lucide-react';
import { ugandaDistricts } from './Districts.jsx';

const SingleCategory = () => {
    const { filterName, setFilterName, eventDate, setEventDate } = useFilterContext();
    let { name } = useParams();
    const { setLoading } = useLoadingContext();
    const [events, setEvents] = useState([]);
    const [filteredData, setFilteredData] = useState(events);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const itemsPerPage = 8; // Increased from 4 to 8
    const { postRequest, getRequest, putRequest, deleteRequest } = useApiRequest();

    const truncateString = (str) => {
        return str.length > 90 ? str.substring(0, 89) + '...' : str;
    };

    function formatDate(date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                await fetchCategories();
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [name]);

    useEffect(() => {
        if (filterName && events && events.length > 0) {
            const filtered = events.filter(event =>
                event.venue_name.toLowerCase().includes(filterName.toLowerCase()) ||
                event.venue_address.toLowerCase().includes(filterName.toLowerCase())
            );
            if (eventDate !== 'all' && events.length > 0) {
                const filtered2 = filterEvents(eventDate, filtered);
                setFilteredData(filtered2);
            } else {
                setFilteredData(filtered);
            }
        } else {
            if (eventDate !== 'all' && events && events.length > 0) {
                const filtered2 = filterEvents(eventDate, events);
                setFilteredData(filtered2);
            } else {
                setFilteredData(events);
            }
        }
        setCurrentPage(1); // Reset to first page when filters change
    }, [events, filterName, eventDate]);

    const fetchCategories = async () => {
        const response = await getRequest(`${domain}/event-details?category=${name}`);
        setEvents(response.body);
    };

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEvents = filteredData?.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData?.length / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Generate page numbers with ellipsis
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    const categoryName = name.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

    return (
        <div key={name} className="min-h-screen bg-gray-50">
            <Header />
            
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-orange-50 to-teal-50 py-8">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center"
                    >
                        <h1 className="text-lg font-bold text-gray-900 mb-4">
                            {categoryName} Events
                        </h1>
                        <p className="text-sm text-gray-600 max-w-2xl mx-auto">
                            Discover amazing {categoryName.toLowerCase()} events happening in your area. 
                            Browse through our curated collection and find your next unforgettable experience.
                        </p>
                        
                        {/* Stats */}
                        <div className="flex justify-center items-center gap-8 mt-6">
                            <div className="text-center">
                                <div className="text-sm font-bold text-orange-500">{filteredData?.length || 0}</div>
                                <div className="text-sm text-gray-600">Total Events</div>
                            </div>
                            <div className="text-center">
                                <div className="text-sm font-bold text-teal-500">{totalPages}</div>
                                <div className="text-sm text-gray-600">Pages</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                {/* Filters Section */}
                <div className="mb-6">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 mb-4">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"></div>
                                <h2 className="text-lg font-semibold text-gray-800">Filter Events</h2>
                            </div>
                            <div className="hidden lg:flex items-center gap-1 text-xs text-gray-500">
                                <span>•</span>
                                <span>Quick filters</span>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="lg:hidden inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200 shadow-sm"
                        >
                            <FilterIcon className="w-4 h-4" />
                            {showFilters ? 'Hide' : 'Show'} Filters
                        </button>
                    </div>
                    
                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0, y: -10 }}
                                animate={{ opacity: 1, height: 'auto', y: 0 }}
                                exit={{ opacity: 0, height: 0, y: -10 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                className="lg:hidden"
                            >
                                <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Event Time</label>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { value: 'all', label: 'All', icon: '📅' },
                                                    { value: 'today', label: 'Today', icon: '⏰' },
                                                    { value: 'week', label: 'This Week', icon: '📆' },
                                                    { value: 'weekend', label: 'Weekend', icon: '🎉' },
                                                    { value: 'month', label: 'This Month', icon: '📅' },
                                                    { value: 'online', label: 'Online', icon: '💻' }
                                                ].map(filter => (
                                                    <button
                                                        key={filter.value}
                                                        onClick={() => setEventDate(filter.value)}
                                                        className={`px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                                                            eventDate === filter.value
                                                                ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-md'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        }`}
                                                    >
                                                        <span>{filter.icon}</span>
                                                        {filter.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                                            <div className="relative">
                                                <select
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all duration-200 appearance-none"
                                                    value={filterName}
                                                    onChange={(e) => setFilterName(e.target.value)}
                                                >
                                                    <option value="">🌍 All Districts</option>
                                                    {ugandaDistricts && ugandaDistricts.map(item =>
                                                        <option key={item.id} value={item.name}>📍 {item.name}</option>
                                                    )}
                                                </select>
                                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    
                    <div className="hidden lg:block">
                        <div className="bg-white rounded-xl p-5 shadow-lg border border-gray-100">
                            <div className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">Event Time</label>
                                    <div className="flex flex-wrap gap-2">
                                        {[
                                            { value: 'all', label: 'All Events', icon: '📅' },
                                            { value: 'today', label: 'Today', icon: '⏰' },
                                            { value: 'week', label: 'This Week', icon: '📆' },
                                            { value: 'weekend', label: 'This Weekend', icon: '🎉' },
                                            { value: 'month', label: 'This Month', icon: '📅' },
                                            { value: 'online', label: 'Online Events', icon: '💻' }
                                        ].map(filter => (
                                            <button
                                                key={filter.value}
                                                onClick={() => setEventDate(filter.value)}
                                                className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 hover:scale-105 ${
                                                    eventDate === filter.value
                                                        ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-lg'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                                                }`}
                                            >
                                                <span className="text-base">{filter.icon}</span>
                                                {filter.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">Location</label>
                                    <div className="relative max-w-md">
                                        <select
                                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all duration-200 appearance-none"
                                            value={filterName}
                                            onChange={(e) => setFilterName(e.target.value)}
                                        >
                                            <option value="">🌍 All Districts</option>
                                            {ugandaDistricts && ugandaDistricts.map(item =>
                                                <option key={item.id} value={item.name}>📍 {item.name}</option>
                                            )}
                                        </select>
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                {(eventDate !== 'all' || filterName !== '') && (
                                    <div className="pt-4 border-t border-gray-100">
                                        <button
                                            onClick={() => {
                                                setEventDate('all');
                                                setFilterName('');
                                            }}
                                            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-red-500 transition-colors duration-200"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Clear all filters
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Events Grid */}
                    <motion.div
                        variants={{
                        initial: { opacity: 0 },
                        animate: { opacity: 1 }
                        }}
                        initial="initial"
                        animate="animate"
                    transition={{ duration: 0.6 }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 mb-8"
                    >
                        {currentEvents && currentEvents.length > 0 ? (
                        currentEvents.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                            >
                                    <EventCard event={item} />
                            </motion.div>
                        ))
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            className="col-span-full"
                        >
                            <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
                                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Calendar className="w-10 h-10 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Events Found</h3>
                                <p className="text-sm text-gray-600 max-w-md mx-auto">
                                    We couldn't find any {categoryName.toLowerCase()} events matching your criteria. 
                                    Try adjusting your filters or check back later for new events.
                                </p>
                            </div>
                        </motion.div>
                        )}
                    </motion.div>

                {/* Enhanced Pagination */}
                    {totalPages > 1 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                    >
                        {/* Page Info */}
                        <div className="text-sm text-gray-600">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData?.length || 0)} of {filteredData?.length || 0} events
                        </div>

                        {/* Pagination Controls */}
                        <div className="flex items-center gap-2">
                            {/* Previous Button */}
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </button>

                            {/* Page Numbers */}
                            <div className="flex items-center gap-1">
                                {getPageNumbers().map((page, index) => (
                                <button
                                        key={index}
                                        onClick={() => typeof page === 'number' && handlePageChange(page)}
                                        disabled={page === '...'}
                                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                                            page === currentPage
                                                ? 'bg-orange-500 text-white'
                                                : page === '...'
                                                ? 'text-gray-400 cursor-default'
                                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {page}
                                </button>
                            ))}
                            </div>

                            {/* Next Button */}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.div>
                    )}
            </div>
            
            <Footer />
        </div>
    );
};

export default SingleCategory;